import { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useGeneralHooks } from "../../../../../../../Hooks/hooks";
import { ICategory, IMenuItemResponce, MenuItems, MenuListResponce } from "../../../../../../../Interfaces/responseTypes";
import { MenuListContentTypes, MenuListTypes } from "../../../../../../../Interfaces/componentTypes";
import { IMenuListProps } from "./menuList";
import useLanguagesHooks from "../../../../../../../Components/Languages/setLanguage-hooks";
import PrimaryCategoryCard from "../../CategoryCard/primary";
import ItemCard from "../../ItemCard/itemCard";
import { MenuOther, RedPlus, } from "../../../../../../../Assets/Icons";
import styles from './menuList.module.scss';

const useMenuListHooks = (props: IMenuListProps) => {
    const {
        type,
        contentType,
        itemActions,
        categoryActions,
        setContentType,
        onSetMenuItemList,
        onMenuItemOrderChange,
        onCategoryOrderChange,
        onOpenAddMenuItemDialog,
        onOpenMenuItemInfoDialog,
    } = props;
    const { activeLanguage } = useLanguagesHooks();
    const { categoryOrderList, itemOrderList, menu, categoryItems, companyId, } = props;
    const [sortedCategories, setSortedCategories] = useState<MenuListResponce | null>(null);
    const { t } = useGeneralHooks();
    const [sortedItems, setSortedItems] = useState<MenuItems | null>(null);

    useEffect(() => {
        if (!menu?.length) {
            setSortedCategories(null);
            return;
        }

        // Keep configured order first, then append categories missing from order_list.
        const orderedCategories = (categoryOrderList ?? [])
            .map((id) => menu.find((item) => item.category.id === id))
            .filter(Boolean);
        const missingCategories = menu.filter(
            (item) => !(categoryOrderList ?? []).includes(item.category.id)
        );
        setSortedCategories([...orderedCategories, ...missingCategories] as MenuListResponce);
    }, [categoryOrderList, menu]);

    useEffect(() => {
        if (!categoryItems?.data) {
            setSortedItems(null);
            return;
        }

        const categoryDataItems = categoryItems.data.items ?? [];
        // Keep configured order first, then append newly created items missing from order_list.
        const orderedItems = (itemOrderList ?? [])
            .map((id) => categoryDataItems.find((item) => item.id === id))
            .filter(Boolean);
        const missingItems = categoryDataItems.filter(
            (item) => !(itemOrderList ?? []).includes(item.id)
        );
        setSortedItems([...orderedItems, ...missingItems] as unknown as MenuItems);
    }, [itemOrderList, categoryItems, menu]);


    const renderHeader = () => {
        if (type === MenuListTypes.VIEW) {
            return contentType === MenuListContentTypes.CATEGORIES ? (
                null
            ) : (
                <div className={styles.menuViewItemsHeader}>
                    <div
                        className={styles.menuListHeaderItem}
                        onClick={() => setContentType(MenuListContentTypes.CATEGORIES)}
                    >
                        <img src={MenuOther} alt="menu.svg" />
                        <p>{t('My_Business.Menu_Box.Categories')}</p>
                    </div>
                    <div className={styles.categoryTitle}>
                        <p>{categoryItems?.title}</p>
                    </div>
                </div>
            );
        } else if (type === MenuListTypes.EDIT) {
            if (contentType === MenuListContentTypes.ITEMS) {
                return (
                    <div className={styles.menuEditItemsHeader}>
                        <div
                            className={styles.menuListHeaderItem}
                            onClick={() => setContentType(MenuListContentTypes.CATEGORIES)}
                        >
                            <img src={MenuOther} alt="menu.svg" />
                            <p>{t('My_Business.Menu_Box.Categories')}</p>
                        </div>
                        <div
                            className={styles.categoryTitle}
                            onClick={() =>
                                onOpenAddMenuItemDialog!(
                                    companyId!,
                                    categoryItems?.data.category.id!,
                                    categoryItems?.title!
                                )
                            }
                        >
                            <p>{categoryItems?.title}</p>
                            <img src={RedPlus} alt="plus.svg" />
                        </div>
                    </div>
                );
            }
        }
        return null;
    };

    const renderContent = () => {
        if (contentType === MenuListContentTypes.CATEGORIES) {
            return sortedCategories?.map((item) => {
                const localizedTitle =
                    item.category[`name_${activeLanguage}` as keyof ICategory] ||
                    item.category.name_en;

                return (
                    <PrimaryCategoryCard
                        key={item.category.id}
                        title={String(localizedTitle)}
                        image={
                            item.category.upload_image
                                ? String(item.category.upload_image)
                                : item.category.default_image
                        }
                        data={item}
                        type={type}
                        order_list={categoryOrderList}
                        onOrderChange={onCategoryOrderChange}
                        actions={type === MenuListTypes.EDIT ? categoryActions : []}
                        onClick={
                            type === MenuListTypes.VIEW
                                ? () => onSetMenuItemList!(item, String(localizedTitle))
                                : undefined
                        }
                    />
                );
            });
        } else if ((contentType === MenuListContentTypes.ITEMS)) {
            if (categoryItems && sortedItems) {
                return sortedItems.map((item) => {
                    const localizedTitle =
                        item[`name_${activeLanguage}` as keyof IMenuItemResponce] ||
                        item.name_en;

                    return (
                        <ItemCard
                            key={item.id}
                            categoryId={categoryItems.data.category.id}
                            data={item}
                            title={String(localizedTitle)}
                            type={type}
                            onOrderChange={onMenuItemOrderChange}
                            order_list={itemOrderList}
                            actions={itemActions!}
                            handleItemIsActive={props.handleItemIsActive}
                            onClick={
                                type === MenuListTypes.VIEW
                                    ?
                                    () => onOpenMenuItemInfoDialog!(categoryItems.data.category.id, item, String(localizedTitle))
                                    :
                                    undefined
                            }
                        />
                    );
                });
            }
        };

        return (
            <div className={styles.loadingContainer}>
                <CircularProgress sx={{ color: '#b00020' }} size="25px" />
            </div>
        );
    };

    return {
        sortedCategories,
        renderContent,
        renderHeader,
    }
};

export default useMenuListHooks